import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { FaArrowLeft as FaArrowLeftRaw, FaCopy as FaCopyRaw, FaInfoCircle as FaInfoCircleRaw, FaPlay as FaPlayRaw } from 'react-icons/fa';
import {
  useCreateMarketingCampaign,
  useMarketingCampaigns,
  useMarketingTemplates,
} from '../hooks/useMarketing';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCopy: any = FaCopyRaw;
const FaInfoCircle: any = FaInfoCircleRaw;
const FaPlay: any = FaPlayRaw;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
`;

const BackBar = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--primary);
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const HeaderCard = styled.section`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: var(--surface);
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--onSurface);
  letter-spacing: -0.02em;
`;

const StatusPillHeader = styled.span<{ $variant: 'sending' | 'delivered' | 'draft' | 'queued' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${({ $variant }) =>
    $variant === 'sending' || $variant === 'queued'
      ? 'var(--primaryContainer)'
      : $variant === 'delivered'
      ? 'var(--successContainer)'
      : 'var(--surfaceVariant)'};
  color: ${({ $variant }) =>
    $variant === 'sending' || $variant === 'queued'
      ? 'var(--primary)'
      : $variant === 'delivered'
      ? 'var(--success)'
      : 'var(--onSurfaceVariant)'};
`;

const Sub = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const Button = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--outline)')};
  background: ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $primary }) => ($primary ? 'var(--onPrimary)' : 'var(--onSurface)')};
  border-radius: 8px;
  padding: 0.5rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const KpiGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.section`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  padding: 1rem 1.15rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
`;

const KpiLabel = styled.div`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.3rem;
  font-weight: 500;
`;

const KpiValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--onSurface);
  letter-spacing: -0.02em;
`;

const KpiHint = styled.div<{ $positive?: boolean; $negative?: boolean }>`
  margin-top: 0.25rem;
  font-size: 0.78rem;
  color: ${({ $positive, $negative }) =>
    $positive ? 'var(--success)' : $negative ? 'var(--error)' : 'var(--onSurfaceVariant)'};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
  gap: 1.5rem;
  align-items: start;
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--onSurface);
`;

const ViewAllLogsLink = styled(Link)`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ProgressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ProgressTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ProgressBar = styled.div`
  height: 12px;
  border-radius: 999px;
  background: var(--outlineVariant);
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => `${Math.min(100, $pct)}%`};
  background: var(--primary);
  border-radius: 999px;
  transition: width 0.2s ease;
`;

const RemainingTag = styled.span`
  display: inline-flex;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  background: var(--primaryContainer);
  color: var(--primary);
`;

const ProgressRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  th,
  td {
    text-align: left;
    padding: 0.75rem 0.6rem;
    border-bottom: 1px solid var(--outlineVariant);
  }
  th {
    font-size: 0.7rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--onSurfaceVariant);
    font-weight: 600;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr:hover {
    background: var(--surfaceVariant);
  }
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 6px;
  height: 6px;
  min-width: 6px;
  min-height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 0.45rem;
  flex-shrink: 0;
`;

const StatusPill = styled.span<{ $tone: 'positive' | 'neutral' | 'negative' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $tone }) =>
    $tone === 'positive' ? 'var(--success)' : $tone === 'negative' ? 'var(--error)' : 'var(--primary)'};
  background: ${({ $tone }) =>
    $tone === 'positive'
      ? 'var(--successContainer)'
      : $tone === 'negative'
      ? 'var(--errorContainer)'
      : 'var(--surfaceVariant)'};
`;

const PhonePreview = styled.div`
  margin: 0 auto 1.25rem auto;
  width: min(280px, 100%);
  border: 6px solid var(--outline);
  border-radius: 28px;
  background: var(--surfaceVariant);
  padding: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const PhoneScreen = styled.div`
  border-radius: 8px;
  background: var(--surface);
  padding: 0.85rem;
  min-height: 120px;
`;

const PhoneBubble = styled.div`
  border-radius: 12px;
  background: var(--primaryContainer);
  padding: 0.75rem 0.9rem;
  font-size: 0.85rem;
  color: var(--onSurface);
  line-height: 1.4;
  word-break: break-word;
`;

const PhonePlayIcon = styled.div`
  margin-top: 0.75rem;
  display: flex;
  justify-content: flex-end;
  color: var(--primary);
  font-size: 1.1rem;
`;

const MetaList = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 0.5rem;
  column-gap: 1rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
  strong {
    color: var(--onSurface);
  }
`;

const SegmentTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const SegmentTag = styled.span`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  background: rgba(100, 120, 200, 0.2);
  color: var(--primary);
`;

const Tip = styled.div`
  margin-top: 1rem;
  border-radius: 10px;
  border: 1px solid var(--primary);
  background: var(--primaryContainer);
  color: var(--onSurface);
  font-size: 0.82rem;
  padding: 0.75rem 0.85rem;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`;

const TipIcon = styled.span`
  color: var(--primary);
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

type ActivityRow = {
  phone: string;
  status: 'Delivered' | 'Failed' | 'Clicked' | 'Pending';
  time: string;
  actionLabel: string;
  actionPath: string;
};

const formatNumber = (value: number) => value.toLocaleString();

const formatRelativeTime = (timestamp: string | null): string => {
  if (!timestamp) return 'Recently';
  const when = new Date(timestamp);
  if (Number.isNaN(when.getTime())) return 'Recently';
  const diffMs = Date.now() - when.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} mins ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return when.toLocaleDateString();
};

const STATUS_DOT_COLOR: Record<string, string> = {
  Delivered: 'var(--success)',
  Clicked: 'var(--primary)',
  Pending: 'var(--warning)',
  Failed: 'var(--error)',
};

export default function MarketingCampaignDetailsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const campaigns = useMarketingCampaigns();
  const templates = useMarketingTemplates();
  const createCampaign = useCreateMarketingCampaign();
  const [duplicating, setDuplicating] = useState(false);

  const campaign = useMemo(
    () => (campaigns.data ?? []).find((item) => item.id === campaignId),
    [campaignId, campaigns.data]
  );

  const templateBody = useMemo(() => {
    if (!campaign?.template_id) return campaign?.name ?? 'No message preview';
    const t = (templates.data ?? []).find((x) => x.id === campaign.template_id);
    return t?.body ?? campaign.name ?? 'No message preview';
  }, [campaign?.template_id, campaign?.name, templates.data]);

  const computed = useMemo(() => {
    const metadata = (campaign?.metadata ?? {}) as Record<string, unknown>;
    const stats =
      typeof metadata.twilio_stats === 'object' && metadata.twilio_stats != null
        ? (metadata.twilio_stats as Record<string, unknown>)
        : {};
    const launch =
      typeof metadata.twilio_last_launch === 'object' && metadata.twilio_last_launch != null
        ? (metadata.twilio_last_launch as Record<string, unknown>)
        : {};

    const recipientsRequested = Number(launch.recipients_requested ?? 0);
    const sent = Number(stats.sent_total ?? launch.sent ?? 0);
    const failed = Number(stats.failed_total ?? launch.failed ?? 0);
    const deliveredPct =
      recipientsRequested > 0 ? Math.round((sent / Math.max(recipientsRequested, 1)) * 100) : 0;
    const clicked = Number(stats.clicked_total ?? launch.clicked ?? 0);
    const optOuts = Number(stats.opt_out_total ?? launch.opt_out_total ?? 0);

    const failures = Array.isArray(launch.failures) ? launch.failures : [];
    const failureRows: ActivityRow[] = failures
      .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
      .slice(0, 6)
      .map((item, index) => ({
        phone: String(item.to ?? `Recipient ${index + 1}`),
        status: 'Failed',
        time: formatRelativeTime(typeof launch.launched_at === 'string' ? launch.launched_at : null),
        actionLabel: 'Details',
        actionPath: '#',
      }));

    const activity = failureRows.length
      ? failureRows
      : [
          {
            phone: 'No recipient logs yet',
            status: 'Delivered' as const,
            time: formatRelativeTime(typeof launch.launched_at === 'string' ? launch.launched_at : null),
            actionLabel: 'View',
            actionPath: '#',
          },
        ];

    return {
      recipientsRequested,
      sent,
      failed,
      clicked,
      optOuts,
      deliveredPct,
      launchedAt: typeof launch.launched_at === 'string' ? launch.launched_at : null,
      activity,
    };
  }, [campaign]);

  const segmentLabels = useMemo(() => {
    if (!campaign) return ['All subscribers'];
    const meta = (campaign.metadata ?? {}) as Record<string, unknown>;
    const seg = meta.segment_names;
    if (Array.isArray(seg)) return seg.filter((s): s is string => typeof s === 'string');
    if (typeof meta.segment_name === 'string') return [meta.segment_name];
    return ['All subscribers'];
  }, [campaign]);

  const handleDuplicate = async () => {
    if (!campaign) return;
    setDuplicating(true);
    try {
      const created = await createCampaign.mutateAsync({
        name: `${campaign.name} (Copy)`,
        channel: campaign.channel,
        templateId: campaign.template_id,
        flowId: campaign.flow_id,
        status: 'draft',
        metadata: {},
      });
      navigate(`/marketing/messages/${created.id}`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to duplicate campaign.');
    } finally {
      setDuplicating(false);
    }
  };

  if (campaigns.isLoading) {
    return (
      <Page>
        <BackBar>
          <BackButton type="button" onClick={() => navigate('/marketing/messages')}>
            <FaArrowLeft size={14} /> Back
          </BackButton>
        </BackBar>
        <div style={{ padding: '1rem', color: 'var(--onSurfaceVariant)' }}>Loading message details...</div>
      </Page>
    );
  }

  if (!campaign) {
    return (
      <Page>
        <BackBar>
          <BackButton type="button" onClick={() => navigate('/marketing/messages')}>
            <FaArrowLeft size={14} /> Back
          </BackButton>
        </BackBar>
        <HeaderCard>
          <TitleBlock>
            <Title>Message not found</Title>
            <Sub>The selected message could not be loaded.</Sub>
          </TitleBlock>
        </HeaderCard>
      </Page>
    );
  }

  const completionPct =
    computed.recipientsRequested > 0
      ? Math.min(100, Math.round(((computed.sent + computed.failed) / computed.recipientsRequested) * 100))
      : 0;

  const remaining = Math.max(0, computed.recipientsRequested - computed.sent - computed.failed);
  const hasLaunched = computed.sent > 0 || computed.failed > 0;
  const statusVariant =
    hasLaunched && remaining > 0
      ? 'sending'
      : hasLaunched && remaining === 0
      ? 'delivered'
      : campaign.status === 'scheduled'
      ? 'queued'
      : 'draft';
  const statusLabel =
    statusVariant === 'sending'
      ? 'Sending'
      : statusVariant === 'delivered'
      ? 'Delivered'
      : statusVariant === 'queued'
      ? 'Queued'
      : 'Draft';

  return (
    <Page>
      <BackBar>
        <BackButton type="button" onClick={() => navigate('/marketing/messages')}>
          <FaArrowLeft size={14} /> Back
        </BackButton>
      </BackBar>
      <HeaderCard>
        <TitleBlock>
          <TitleRow>
            <Title>{campaign.name}</Title>
            <StatusPillHeader $variant={statusVariant}>{statusLabel}</StatusPillHeader>
          </TitleRow>
          <Sub>
            {completionPct}% Complete
            {computed.launchedAt ? ` • Started ${formatRelativeTime(computed.launchedAt)}` : ''}
          </Sub>
        </TitleBlock>
        <HeaderActions>
          <Button type="button">Export Report</Button>
          <Button type="button" onClick={handleDuplicate} disabled={duplicating}>
            <FaCopy size={12} /> {duplicating ? 'Duplicating…' : 'Duplicate'}
          </Button>
          <Button $primary type="button">Pause Campaign</Button>
        </HeaderActions>
      </HeaderCard>

      <KpiGrid>
        <KpiCard>
          <KpiLabel>Total Recipients</KpiLabel>
          <KpiValue>{formatNumber(computed.recipientsRequested)}</KpiValue>
          <KpiHint $positive={computed.recipientsRequested > 0}>100% of segment</KpiHint>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Delivered</KpiLabel>
          <KpiValue>{formatNumber(computed.sent)}</KpiValue>
          <KpiHint $positive={computed.deliveredPct >= 90}>{computed.deliveredPct}% delivery rate</KpiHint>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Clicked</KpiLabel>
          <KpiValue>{formatNumber(computed.clicked)}</KpiValue>
          <KpiHint $positive={computed.clicked > 0}>
            {computed.recipientsRequested > 0
              ? `${((computed.clicked / computed.recipientsRequested) * 100).toFixed(1)}% CTR`
              : 'Message engagement'}
          </KpiHint>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Opt-outs</KpiLabel>
          <KpiValue>{formatNumber(computed.optOuts)}</KpiValue>
          <KpiHint $negative={computed.optOuts > 0}>
            {computed.recipientsRequested > 0
              ? `${((computed.optOuts / computed.recipientsRequested) * 100).toFixed(1)}% churn rate`
              : 'Unsubscribed contacts'}
          </KpiHint>
        </KpiCard>
      </KpiGrid>

      <MainGrid>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <Card>
            <CardTitle>Delivery Progress</CardTitle>
            <ProgressWrap>
              <ProgressTopRow>
                <span style={{ fontSize: '0.85rem', color: 'var(--onSurfaceVariant)' }}>
                  {formatNumber(computed.sent)} sent ({completionPct}%) out of {formatNumber(computed.recipientsRequested)} total
                </span>
                {remaining > 0 && <RemainingTag>{formatNumber(remaining)} Remaining</RemainingTag>}
              </ProgressTopRow>
              <ProgressBar>
                <ProgressFill $pct={completionPct} />
              </ProgressBar>
              <ProgressRow>
                <span>{formatNumber(computed.sent)} delivered</span>
                <span>{formatNumber(computed.failed)} failed</span>
              </ProgressRow>
            </ProgressWrap>
          </Card>

          <Card>
            <CardHeaderRow>
              <CardTitle>Recent Activity</CardTitle>
              <ViewAllLogsLink to="#">View All Logs</ViewAllLogsLink>
            </CardHeaderRow>
            <Table>
              <thead>
                <tr>
                  <th>Phone Number</th>
                  <th>Status</th>
                  <th>Time</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {computed.activity.map((row, index) => (
                  <tr key={`${row.phone}-${index}`}>
                    <td>{row.phone}</td>
                    <td>
                      <StatusPill
                        $tone={
                          row.status === 'Failed'
                            ? 'negative'
                            : row.status === 'Clicked'
                            ? 'neutral'
                            : 'positive'
                        }
                      >
                        <StatusDot $color={STATUS_DOT_COLOR[row.status] ?? 'var(--onSurfaceVariant)'} />
                        {row.status}
                      </StatusPill>
                    </td>
                    <td>{row.time}</td>
                    <td>
                      <Link to={row.actionPath} style={{ color: 'var(--primary)', fontWeight: 600 }}>
                        {row.actionLabel}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </div>

        <Card>
          <CardTitle>Campaign Overview</CardTitle>
          <PhonePreview>
            <PhoneScreen>
              <PhoneBubble>{templateBody}</PhoneBubble>
              <PhonePlayIcon>
                <FaPlay size={14} />
              </PhonePlayIcon>
            </PhoneScreen>
          </PhonePreview>
          <MetaList>
            <span>Created By</span>
            <strong>{campaign.created_by ?? 'Marketing Team'}</strong>
            <span>Sent On</span>
            <strong>
              {computed.launchedAt
                ? new Date(computed.launchedAt).toLocaleDateString(undefined, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : 'Not launched yet'}
            </strong>
            <span>Segments Targeted</span>
            <strong />
          </MetaList>
          <SegmentTags>
            {segmentLabels.map((label) => (
              <SegmentTag key={label}>{label}</SegmentTag>
            ))}
          </SegmentTags>
          <Tip>
            <TipIcon>
              <FaInfoCircle size={16} />
            </TipIcon>
            <span>
              Optimization Tip: If click-through stays under 15%, consider a follow-up blast with a tighter CTA or different segment.
            </span>
          </Tip>
        </Card>
      </MainGrid>
    </Page>
  );
}
